import { type FormatPrefs, formatAgo, formatMonthDay, formatTime } from '@trips/shared/format';
import type { FlightLive, ItemChange } from '@trips/shared/schema';
import { localDate } from '@trips/shared/time';
import { useMemo, useState } from 'react';
import { useStore } from '../../data/store';
import { syncNow } from '../../data/sync';
import { api } from '../../lib/api';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/Field';
import { Sheet } from '../../ui/Sheet';
import { toast } from '../../ui/Toast';
import s from './FlightLive.module.css';

/** "Updated 1 min ago", a refresh button, and "Change flight" for a rebooking. */
export function LiveBar({
  itemId,
  live,
  now,
  tz,
  prefs,
  date,
}: {
  itemId: string;
  live: Partial<FlightLive> | undefined;
  now: number;
  tz: string;
  prefs: FormatPrefs;
  /** The flight's local departure date, to start "Change flight" from. */
  date: string;
}) {
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const checked = live?.lastCheckedAt ?? live?.lastPolledAt ?? null;

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api<{ live: boolean; changes?: number }>(
        `/api/flights/${encodeURIComponent(itemId)}/refresh?force=1`,
        { method: 'POST' },
      );
      await syncNow();
      if (!r.live) toast('Live tracking is off on the server.', { tone: 'error' });
      else toast(r.changes ? `${r.changes} change${r.changes > 1 ? 's' : ''}` : 'Up to date');
    } catch {
      toast("Couldn't reach the server. The estimate on this phone carries on.", {
        tone: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={s.bar}>
      <span className="t-footnote muted num">
        {checked ? `Updated ${formatAgo(checked, now, tz, prefs)}` : 'Not checked yet'}
        {live?.alertId ? ' · alerts on' : ''}
      </span>
      <span className={s.actions}>
        <Button size="sm" icon="arrows-clockwise" disabled={busy} onClick={() => void refresh()}>
          {busy ? 'Checking' : 'Refresh'}
        </Button>
        <Button size="sm" icon="airplane-tilt" onClick={() => setChanging(true)}>
          Change flight
        </Button>
      </span>
      <ChangeFlight
        itemId={itemId}
        open={changing}
        onClose={() => setChanging(false)}
        date={date}
      />
    </div>
  );
}

function ChangeFlight({
  itemId,
  open,
  onClose,
  date,
}: {
  itemId: string;
  open: boolean;
  onClose: () => void;
  date: string;
}) {
  const [flight, setFlight] = useState('');
  const [day, setDay] = useState(date);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api<{ flight: string }>(`/api/flights/${encodeURIComponent(itemId)}/change`, {
        method: 'POST',
        body: { flight, date: day },
      });
      await syncNow();
      toast(`Now on ${r.flight}. The old flight is in the history.`);
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't change the flight.", { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Change flight"
      actions={
        <Button
          variant="primary"
          disabled={busy || flight.trim().length < 3}
          onClick={() => void save()}
        >
          {busy ? 'Looking it up' : 'Change'}
        </Button>
      }
    >
      <p className="t-footnote muted">
        Moved to another flight? Enter the new one. Its times, gate and alerts replace this one.
      </p>
      <TextField
        label="New flight"
        value={flight}
        onChange={(v) => setFlight(v.toUpperCase())}
        placeholder="AA98"
        autoCapitalize="characters"
      />
      <TextField label="Departure date" type="date" value={day} onChange={setDay} />
    </Sheet>
  );
}

const LABELS: Record<string, string> = {
  scheduledOut: 'Scheduled departure',
  estimatedOut: 'Departure',
  actualOut: 'Left the gate',
  actualOff: 'Took off',
  scheduledIn: 'Scheduled arrival',
  estimatedIn: 'Arrival',
  actualOn: 'Landed',
  actualIn: 'At the gate',
  liveStatus: 'Status',
  gateOrigin: 'Gate',
  terminalOrigin: 'Terminal',
  gateDestination: 'Arrival gate',
  terminalDestination: 'Arrival terminal',
  baggageClaim: 'Baggage belt',
  aircraftType: 'Aircraft',
  registration: 'Tail',
  divertedTo: 'Diverted to',
  operatingIdent: 'Operated as',
  startAt: 'Departure',
  endAt: 'Arrival',
  title: 'Flight',
  status: 'Booking',
  details: 'Details',
};
const ARRIVAL = new Set(['scheduledIn', 'estimatedIn', 'actualOn', 'actualIn', 'endAt']);
const SOURCE: Record<string, string> = {
  push: 'pushed',
  poll: 'checked',
  email: 'email',
  manual: 'by hand',
};

/** Every change to this flight, newest first. */
export function FlightHistory({
  itemId,
  depTz,
  arrTz,
  prefs,
  now,
}: {
  itemId: string;
  depTz: string;
  arrTz: string;
  prefs: FormatPrefs;
  now: number;
}) {
  const all = useStore((st) => st.tables.itemChanges);
  const list = useMemo(
    () =>
      [...all.values()]
        .filter((c: ItemChange) => c.itemId === itemId && !c.deleted)
        .sort((a, b) => b.at - a.at)
        .slice(0, 40),
    [all, itemId],
  );
  if (!list.length) return null;
  const show = (field: string, v: unknown): string => {
    if (v == null || v === '') return '–';
    if (typeof v === 'number' && v > 1e12) {
      const tz = ARRIVAL.has(field) ? arrTz : depTz;
      return formatTime(v, tz, prefs);
    }
    if (typeof v === 'object') {
      const d = v as { airline?: string; flightNumber?: string; seat?: string };
      return d.flightNumber ? `${d.airline ?? ''}${d.flightNumber}` : '';
    }
    return String(v).replace('_', ' ');
  };
  return (
    <ol className={s.history}>
      {list.map((c) => {
        const lines = c.changes
          .filter((x) => LABELS[x.field])
          .map((x) => {
            const from = show(x.field, x.from);
            const to = show(x.field, x.to);
            return from === to ? null : `${LABELS[x.field]}: ${from} → ${to}`;
          })
          .filter(Boolean);
        if (!lines.length) return null;
        const day = localDate(c.at, depTz);
        return (
          <li key={c.id} className={s.change}>
            <span className={`${s.when} t-footnote muted num`}>
              {now - c.at < 86_400_000
                ? formatAgo(c.at, now, depTz, prefs)
                : `${formatMonthDay(day, prefs)} ${formatTime(c.at, depTz, prefs)}`}
              {c.kind === 'rebook' ? ' · rebooked' : ''}
              {c.source ? ` · ${SOURCE[c.source]}` : ''}
            </span>
            {lines.map((l) => (
              <span key={l} className={`${s.line} num`}>
                {l}
              </span>
            ))}
          </li>
        );
      })}
    </ol>
  );
}
